import { useQuery, useQueryClient } from '@tanstack/react-query'
import { swimApi } from '@swim/api-client'
import { QUERY_KEYS } from '@/lib/queryKeys'

export interface SessionUser {
  id: string
  email: string
  name: string | null
  image: string | null
  emailVerified: boolean
  role: string | null
  ageConfirmedAt: string | null
}

export interface SessionWorkspace {
  id: string
  businessName: string
  status: string
}

export interface SessionData {
  user: SessionUser | null
  session: {
    id: string
    expiresAt: string
    userId: string
  } | null
  workspace: SessionWorkspace | null
}

const STALE_TIME = 0  // always refetch on window focus — role changes (CUSTOMER→MERCHANT) must propagate immediately

/**
 * Returns the current authenticated session.
 *
 * @remarks
 * Session is stored in an HTTP-only cookie. This hook reads it by calling
 * GET /auth/customer/session with credentials. Returns null user when no
 * session exists — never throws for unauthenticated state.
 *
 * Used everywhere auth state is needed: ProtectedRoute, AvatarMenu, profile pages.
 */
export function useSession() {
  const { data, isLoading, error } = useQuery<SessionData>({
    queryKey: QUERY_KEYS.account.me(),
    queryFn: async () => {
      const result = await swimApi.get<SessionData>('/account/me')
      if (!result.success) {
        // 401 = expired/no session → return null, ProtectedRoute redirects to sign-in
        // 5xx/network → throw so React Query keeps last cached user (no false logout)
        // Error state surfaces via useSession().error — UI layer handles user feedback
        if (result.status === 401) return { user: null, session: null, workspace: null }
        throw new Error(result.error ?? 'Session check failed')
      }
      return result.data
    },
    staleTime: STALE_TIME,
    retry: false,
  })

  const role = data?.user?.role ?? null

  return {
    user: data?.user ?? null,
    session: data?.session ?? null,
    workspace: data?.workspace ?? null,
    isLoading,
    isAuthenticated: Boolean(data?.user),
    // Member ADMIN/SUPER_ADMIN are staff roles with no merchant authority (bead Web-p1cnx).
    isMerchant: role === 'MERCHANT',
    error,
  }
}

/**
 * Clears all user-scoped query cache and refetches the session.
 * Call after every auth state change: sign-in, sign-out, email verify.
 *
 * @remarks
 * TanStack Query returns cached data regardless of `enabled` state — so stale
 * data from a previous authenticated user can appear for a newly signed-in user
 * if only the session query is invalidated. We remove all user-scoped keys
 * (account.*, drops.*, claims.*) before refetching, guaranteeing a clean slate.
 * Public queries (streams, offers) are intentionally preserved — they are not
 * user-specific and are expensive to re-fetch unnecessarily.
 */
export function useInvalidateSession() {
  const queryClient = useQueryClient()
  return async () => {
    // Wipe user-scoped cache — prevents cross-user data leaks on auth change
    queryClient.removeQueries({ queryKey: ['account'] })
    queryClient.removeQueries({ queryKey: ['drops'] })
    queryClient.removeQueries({ queryKey: ['claims'] })
    queryClient.removeQueries({ queryKey: ['disputes'] })
    queryClient.removeQueries({ queryKey: ['data-rights'] })
    // Refetch session and wait — callers can read fresh user/role state after awaiting
    return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.account.me() })
  }
}
